import React from 'react';
import { TourQuestionnaire, TouristSite, Location } from '../../../types';
import { cn } from '../../../lib/utils';
import { CheckCircle2, Star, Info, Home } from 'lucide-react';
import { getDistance } from '../../../services/circuitEngine';

interface Step9_7Props {
  questionnaire: TourQuestionnaire;
  setQuestionnaire: React.Dispatch<React.SetStateAction<TourQuestionnaire>>;
  sites: TouristSite[];
  currentDay: number;
  setSelectedActivity: (site: TouristSite) => void;
  setPreviousStep: (step: number) => void;
  setStep: (step: number) => void;
  handleNext: () => void;
  handleBack: () => void;
  proposedCircuit: any;
}

export const Step9_7: React.FC<Step9_7Props> = ({
  questionnaire,
  setQuestionnaire,
  sites,
  currentDay,
  setSelectedActivity,
  setPreviousStep,
  setStep,
  handleNext,
  handleBack,
  proposedCircuit,
}) => {
  // Find end-of-day location from the proposed circuit
  let endOfDayLocation: Location | null = null;

  if (proposedCircuit?.steps) {
    let dayCount = 0;
    let lastSiteLocation: Location | null = null;
    for (const step of proposedCircuit.steps) {
      if (step.type === 'pickup') dayCount++;
      if (step.type === 'activity' && step.site && dayCount === currentDay) {
        lastSiteLocation = step.site.location;
      }
      if (dayCount > currentDay) break;
    }
    endOfDayLocation = lastSiteLocation || questionnaire.pickupLocation;
  }

  const relevantAccommodations = sites
    .filter(s => {
      if (!s.isAccommodation) return false;
      if (s.sector !== questionnaire.sector) return false;
      if (endOfDayLocation) {
        const dist = getDistance(endOfDayLocation, s.location);
        if (dist > 30) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (endOfDayLocation) {
        const dA = getDistance(endOfDayLocation, a.location);
        const dB = getDistance(endOfDayLocation, b.location);
        return dA - dB;
      }
      return (b.rating || b.googleRating || 0) - (a.rating || a.googleRating || 0);
    });

  return (
    <div className="flex flex-col h-full p-0 overflow-hidden bg-zinc-950">
      <div className="shrink-0 p-6 space-y-2">
        <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
          Hébergement — Nuit {currentDay}
        </h2>
        <p className="text-zinc-400 text-xs">
          Hébergements recommandés à proximité de la fin de votre journée.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 space-y-3 custom-scrollbar pb-32">
        {relevantAccommodations.length > 0 ? (
          relevantAccommodations.map(site => {
            const isSelected = questionnaire.selectedAccommodations?.[currentDay] === site.id;

            return (
              <div key={site.id} className="relative">
                <button
                  onClick={() => {
                    const selectedAccommodations = { ...(questionnaire.selectedAccommodations || {}) };
                    selectedAccommodations[currentDay] = site.id;
                    setQuestionnaire(prev => ({
                      ...prev,
                      selectedAccommodations,
                      accommodationCoords: site.location,
                      accommodationAddress: site.address || site.name,
                    }));
                  }}
                  className={cn(
                    "w-full p-3.5 text-left transition-all flex items-center gap-4 rounded-xl border backdrop-blur-sm",
                    isSelected
                      ? "bg-emerald-500/10 border-emerald-500"
                      : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                  )}
                >
                  <div className="w-16 h-16 rounded-lg bg-zinc-800 overflow-hidden shrink-0 border border-white/10 relative">
                    {site.image && site.image.length > 0 && !site.image.includes('maps.googleapis.com') ? (
                      <img src={site.image} alt={site.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">🏡</div>
                    )}
                    {isSelected && (
                      <div className="absolute top-0 left-0 w-7 h-7 bg-emerald-500 rounded-br-xl flex items-center justify-center shadow-lg z-10">
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <h3 className="font-bold text-white text-sm leading-tight truncate">{site.name}</h3>
                    </div>
                    <p className="text-[10px] text-zinc-400 line-clamp-2 mb-2 leading-relaxed">{site.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-amber-400">
                        <Star className="w-3 h-3 fill-current" />
                        <span className="text-[10px] font-bold">{site.rating || site.googleRating || "N/A"}</span>
                      </div>
                      {endOfDayLocation && (
                        <span className="text-[10px] text-zinc-500 font-medium">
                          à {getDistance(endOfDayLocation, site.location).toFixed(1)} km
                        </span>
                      )}
                    </div>
                  </div>
                </button>

                <button
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedActivity(site);
                    setPreviousStep(9.7);
                    setStep(10);
                  }}
                  className="absolute top-3 right-3 p-2 bg-zinc-800/80 hover:bg-zinc-700 rounded-full text-zinc-300 transition-colors z-20"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
            );
          })
        ) : (
          <div className="p-12 text-center bg-zinc-900/50 rounded-3xl border border-zinc-800 border-dashed">
            <Home className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 text-sm leading-relaxed">
              Aucun hébergement correspondant n'a été trouvé dans ce secteur.
            </p>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex gap-3">
          <button
            onClick={handleBack}
            className="flex-1 py-3 bg-zinc-900 text-zinc-400 rounded-xl font-semibold text-sm hover:bg-zinc-800 border border-zinc-800 transition-colors"
          >
            Retour
          </button>
          <button
            onClick={handleNext}
            className="flex-[2] py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)]"
          >
            Valider l'hébergement
          </button>
        </div>
      </div>
    </div>
  );
};
